import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, lastValueFrom, from } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';

const GRAPH_SCOPES = ['https://graph.microsoft.com/.default'];

@Injectable({
    providedIn: 'root'
})
export class GraphService {
    constructor(
        private http: HttpClient,
        private msalService: MsalService
    ) { }

    private async getGraphToken(): Promise<string> {
        const account = this.msalService.instance.getActiveAccount();


        if (!account) {
            throw new Error('[GraphService] No active MSAL account. User must be logged in.');
        }

        // Retry logic: MSAL may not be fully initialized on first attempt
        let retries = 0;
        const maxRetries = 3;
        
        while (retries < maxRetries) {
            try {
                const tokenResponse = await lastValueFrom(
                    this.msalService.acquireTokenSilent({
                        scopes: GRAPH_SCOPES,
                        account: account
                    })
                );

                return tokenResponse.accessToken;
            } catch (error: any) {
                retries++;
                
                if (retries >= maxRetries) {
                    throw error;
                }
                
                // Wait before retrying
                await new Promise(resolve => setTimeout(resolve, 500));
            }
        }

        throw new Error('[GraphService] Failed to acquire graph token after retries');
    }

    public async getUserPhoto(): Promise<any> {
        const p = new Promise<any>((resolve, reject) => {
            this.getGraphToken().then(graphToken => {
                let url = "https://graph.microsoft.com/v1.0/me/photo/$value";
                let headers: HttpHeaders = new HttpHeaders({
                    Authorization: `Bearer ${graphToken}`
                });


                let stream$ = this.http.get(url, { headers: headers, responseType: "blob" });

                if (stream$) {
                    lastValueFrom(stream$).then((response: any) => {
                        if (response) {
                            let reader: FileReader = new FileReader();

                            reader.readAsDataURL(response);
                            reader.onloadend = () => {
                                let photo = reader.result;
                                resolve(photo);
                            };
                        }
                    }).catch(err => {
                        reject(err);
                    });
                }
            }).catch(err => {
                reject(err);
            });
        });

        return await p;
    }

    public getMemberOf(): Observable<any[]> {
        return from(this.getGraphToken()).pipe(
            switchMap(graphToken => {
                const headers = new HttpHeaders({ Authorization: `Bearer ${graphToken}` });
                return this.http.get<any>('https://graph.microsoft.com/v1.0/me/memberOf', { headers });
            }),
            map((response: any) => response?.value || [])
        );
    }

    public async getUserGroups(role: any): Promise<any> {
        let results: any = null;

        try {
            const graphToken = await this.getGraphToken();

            let url = `https://graph.microsoft.com/v1.0/me/transitiveMemberOf/microsoft.graph.group?$count=true&$orderby=displayName&$filter=startswith(displayName, '${role}')`;
            let headers: HttpHeaders = new HttpHeaders({
                Authorization: `Bearer ${graphToken}`,
                ConsistencyLevel: "eventual"
            });

            let stream$ = this.http.get(url, { headers: headers });
            results = await lastValueFrom(stream$);

        } catch (error: any) {
            throw error;
        }

        return results;
    }

    /**
     * Checks if the current user is a member of a specific AD group by object ID
     * @param requiredGroupObjectId The object ID of the AD group to check membership for
     * @returns Promise with boolean indicating membership
     */
    public async isUserInRequiredGroup(requiredGroupObjectId: string): Promise<boolean> {
        try {

            const graphToken = await this.getGraphToken();
            
            const headers = new HttpHeaders({ Authorization: `Bearer ${graphToken}` });

            // Preferred path: use checkMemberGroups for exact membership check.
            // This avoids paging issues when user belongs to >100 groups.
            try {
                const checkUrl = 'https://graph.microsoft.com/v1.0/me/checkMemberGroups';
                const checkPayload = { groupIds: [requiredGroupObjectId] };


                const checkResponse = await lastValueFrom(
                    this.http.post<any>(checkUrl, checkPayload, { headers })
                );

                const matchedIds: string[] = checkResponse?.value || [];
                const isMemberByCheck = matchedIds.some((id: string) =>
                    String(id || '').toLowerCase() === String(requiredGroupObjectId || '').toLowerCase()
                );

              

                if (isMemberByCheck) {
                    return true;
                }
            } catch (checkError: any) {
                // checkMemberGroups failed, falling back to transitiveMemberOf
            }

            // Fallback: use transitiveMemberOf so nested group membership is included.
            let url = 'https://graph.microsoft.com/v1.0/me/transitiveMemberOf/microsoft.graph.group?$select=id,displayName&$top=999';
            const userGroups: any[] = [];


            while (url) {
                const response = await lastValueFrom(this.http.get<any>(url, { headers }));
                const groupsList = response?.value || [];
                userGroups.push(...groupsList);
                url = response?.['@odata.nextLink'] || '';
            }


            const isMember = userGroups.some((group: any) =>
                String(group.id || '').toLowerCase() === String(requiredGroupObjectId || '').toLowerCase()
            );

           

            return isMember;
        } catch (error: any) {
           throw error;
        }
    }
}
 